import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Store, HeartHandshake, CreditCard, Package, Utensils, Truck, ShieldCheck, ShieldX, HelpCircle, Landmark, Trash2, UserPlus, Power } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [helpOrders, setHelpOrders] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [allNeeds, setAllNeeds] = useState([]);
  const [infoFor, setInfoFor] = useState(null);
  const [infoMsg, setInfoMsg] = useState("");

  const load = useCallback(async () => {
    const [s, u, l, h, o, r, rq, nd] = await Promise.all([
      api.get("/stats"), api.get("/admin/users"), api.get("/admin/listings"),
      api.get("/admin/help-orders"), api.get("/admin/orgs"), api.get("/admin/remittances"),
      api.get("/admin/requests"), api.get("/admin/needs"),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setListings(l.data);
    setHelpOrders(h.data);
    setOrgs(o.data);
    setLedger(r.data);
    setReqs(rq.data);
    setAllNeeds(nd.data);
  }, []);

  const deleteRequest = async (id) => { await api.delete(`/admin/requests/${id}`); toast.success("Request removed"); load(); };
  const deleteNeed = async (id) => { await api.delete(`/admin/needs/${id}`); toast.success("Wishlist item removed"); load(); };
  const resetCounters = async () => {
    if (!window.confirm("Clear the money counter? This resets Total raised and removes all recorded transfers. Payment records/receipts are kept. This can't be undone.")) return;
    await api.post("/admin/transparency/reset"); toast.success("Money counter cleared"); load();
  };
  const toggleSub = async (id) => { await api.post(`/admin/users/${id}/subscription`); toast.success("Subscription updated"); load(); };
  const deleteUser = async (id) => {
    if (!window.confirm("Delete this subscriber? Their account and listings will be removed.")) return;
    await api.delete(`/admin/users/${id}`); toast.success("Subscriber deleted"); load();
  };
  const addSubscriber = async (payload) => {
    try { await api.post("/admin/subscribers", payload); toast.success("Subscriber added"); load(); return true; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
  };
  const subscribers = users.filter((u) => u.role === "grocery_store");

  const ship = async (id) => {
    await api.post(`/admin/help-orders/${id}/ship`);
    toast.success("Marked as shipped");
    load();
  };

  const verifyOrg = async (id) => { await api.post(`/admin/orgs/${id}/verify`); toast.success("Organization verified — now public"); load(); };
  const rejectOrg = async (id) => { await api.post(`/admin/orgs/${id}/reject`, { message: "Verification could not be completed." }); toast.success("Organization rejected"); load(); };
  const requestInfo = async () => {
    if (!infoMsg.trim()) { toast.error("Add a note about what's needed"); return; }
    await api.post(`/admin/orgs/${infoFor.id}/request-info`, { message: infoMsg });
    toast.success("Info request sent to the organization");
    setInfoFor(null); setInfoMsg(""); load();
  };

  const addRemittance = async (payload) => {
    try {
      await api.post("/admin/remittances", payload);
      toast.success("Transfer recorded");
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    }
  };
  const deleteRemittance = async (id) => { await api.delete(`/admin/remittances/${id}`); toast.success("Removed"); load(); };

  useEffect(() => { load(); }, [load]);

  const pendingOrgs = orgs.filter((o) => ["pending", "info_requested"].includes(o.verification_status));

  const toggle = async (id) => {
    const { data } = await api.post(`/admin/users/${id}/toggle`);
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active: data.active } : u)));
    toast.success(data.active ? "Account activated" : "Account suspended");
  };

  return (
    <DashboardShell title="Admin control room" subtitle="Oversee stores, organizations and rescued food." showBell={false}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
        <Stat icon={Store} label="Stores" value={stats?.stores} />
        <Stat icon={HeartHandshake} label="Organizations" value={stats?.orgs} />
        <Stat icon={CreditCard} label="Active subs" value={stats?.active_subs} accent="emerald" />
        <Stat icon={Package} label="Listings" value={stats?.listings} />
        <Stat icon={Utensils} label="Meals rescued" value={stats?.meals_rescued} accent="orange" />
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-stone-100 rounded-full p-1">
          <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-full data-[state=active]:bg-white">Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-listings" value="listings" className="rounded-full data-[state=active]:bg-white">Listings</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-fulfillment" value="fulfillment" className="rounded-full data-[state=active]:bg-white">Fulfillment</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-approvals" value="approvals" className="rounded-full data-[state=active]:bg-white">
            Approvals{pendingOrgs.length > 0 ? ` (${pendingOrgs.length})` : ""}
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-payouts" value="payouts" className="rounded-full data-[state=active]:bg-white">Charity payouts</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-requests" value="requests" className="rounded-full data-[state=active]:bg-white">Requests & wishlists</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-subscribers" value="subscribers" className="rounded-full data-[state=active]:bg-white">Subscribers</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
            <Table data-testid="admin-users-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <p className="font-medium text-stone-900">{u.org_name}</p>
                      <p className="text-xs text-stone-500">{u.email}</p>
                    </TableCell>
                    <TableCell className="capitalize">{u.role.replace("_", " ")}</TableCell>
                    <TableCell>{u.city} {u.zip_code && `· ${u.zip_code}`}</TableCell>
                    <TableCell>
                      {u.role === "grocery_store" ? (
                        <Badge variant="outline" className={`rounded-full ${u.subscription_status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-stone-100 text-stone-500"}`}>
                          {u.subscription_status}
                        </Badge>
                      ) : <span className="text-stone-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch data-testid={`toggle-${u.id}`} checked={u.active} onCheckedChange={() => toggle(u.id)} />
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8">No users yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="listings" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
            <Table data-testid="admin-listings-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Food</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Claimed by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium text-stone-900">{l.food_type}</TableCell>
                    <TableCell>{l.store_name}</TableCell>
                    <TableCell>{l.quantity}{l.unit}</TableCell>
                    <TableCell>{l.city}</TableCell>
                    <TableCell className="capitalize">{l.status}</TableCell>
                    <TableCell>{l.claimed_by_name || "—"}</TableCell>
                  </TableRow>
                ))}
                {listings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-8">No listings yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="fulfillment" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-4 text-sm text-stone-600 border-b border-stone-100 bg-orange-50/60">
              Paid "Help Someone Today" orders. Purchase each item on Amazon and ship to the pickup organization, then mark shipped.
              <span className="font-medium text-orange-800"> (Amazon purchase is completed manually by your team — there is no public auto-buy API.)</span>
            </div>
            <Table data-testid="admin-help-orders-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Item</TableHead>
                  <TableHead>For</TableHead>
                  <TableHead>Ship to (org)</TableHead>
                  <TableHead>Donor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {helpOrders.map((o) => (
                  <TableRow key={o.id} data-testid={`help-order-${o.id}`}>
                    <TableCell className="font-medium text-stone-900">{o.item_name}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full capitalize">{o.type === "individual" ? "individual" : "org need"}</Badge></TableCell>
                    <TableCell><p>{o.org_name}</p><p className="text-xs text-stone-500 max-w-[220px] truncate">{o.org_address || "—"}</p></TableCell>
                    <TableCell>{o.donor_name}{o.donor_email ? <span className="block text-xs text-stone-500">{o.donor_email}</span> : null}</TableCell>
                    <TableCell>${Number(o.amount).toFixed(2)}</TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-full capitalize ${o.status === "shipped" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-orange-50 text-orange-800 border-orange-200"}`}>{o.status}</Badge></TableCell>
                    <TableCell className="text-right">{o.status === "paid" && <Button data-testid={`ship-${o.id}`} onClick={() => ship(o.id)} variant="outline" className="rounded-full"><Truck className="w-4 h-4 mr-2" />Mark shipped</Button>}</TableCell>
                  </TableRow>
                ))}
                {helpOrders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-stone-500 py-8">No help orders yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="approvals" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="approvals-panel">
            <div className="p-4 text-sm text-stone-600 border-b border-stone-100 bg-emerald-50/60">
              Review aid organizations before they appear on the public map. Vulnerable people rely on these listings — approve only legitimate shelters and nonprofits.
            </div>
            <Table data-testid="approvals-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Organization</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs.map((o) => (
                  <TableRow key={o.id} data-testid={`approval-row-${o.id}`}>
                    <TableCell>
                      <p className="font-medium text-stone-900 flex items-center gap-1.5">
                        {o.org_name}
                        {o.verified && <ShieldCheck className="w-4 h-4 text-emerald-700" title="Verified" />}
                      </p>
                      <p className="text-xs text-stone-500">{o.email}{o.is_warming_shelter ? " · warming shelter" : ""}</p>
                    </TableCell>
                    <TableCell>{o.address || o.city} {o.zip_code && `· ${o.zip_code}`}</TableCell>
                    <TableCell className="text-sm text-stone-600">{o.phone || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full capitalize ${
                        o.verification_status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : o.verification_status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-orange-50 text-orange-800 border-orange-200"}`}>
                        {(o.verification_status || "pending").replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {o.verified ? (
                        <Button data-testid={`revoke-${o.id}`} onClick={() => rejectOrg(o.id)} variant="outline" size="sm" className="rounded-full text-red-600"><ShieldX className="w-4 h-4 mr-1.5" />Revoke</Button>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button data-testid={`verify-${o.id}`} onClick={() => verifyOrg(o.id)} size="sm" className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white"><ShieldCheck className="w-4 h-4 mr-1.5" />Approve</Button>
                          <Button data-testid={`info-${o.id}`} onClick={() => { setInfoFor(o); setInfoMsg(""); }} variant="outline" size="sm" className="rounded-full"><HelpCircle className="w-4 h-4 mr-1.5" />Info</Button>
                          <Button data-testid={`reject-${o.id}`} onClick={() => rejectOrg(o.id)} variant="outline" size="sm" className="rounded-full text-red-600"><ShieldX className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {orgs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8">No organizations yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="payouts" className="mt-6 space-y-6">
          <div className="grid sm:grid-cols-3 gap-5" data-testid="payout-totals">
            <Stat icon={CreditCard} label="Total raised" value={money(ledger?.total_raised)} isText />
            <Stat icon={Landmark} label="Total donated to charity" value={money(ledger?.total_remitted)} accent="emerald" isText />
            <Stat icon={HeartHandshake} label="Awaiting transfer" value={money(Math.max(0, ledger?.balance_pending || 0))} accent="orange" isText />
          </div>
          <RemittanceForm onSubmit={addRemittance} />
          <div className="flex justify-end">
            <Button data-testid="clear-counter-btn" onClick={resetCounters} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" /> Clear money counter
            </Button>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-4 text-sm text-stone-600 border-b border-stone-100 bg-emerald-50/60">
              Every transfer you record here appears on the public <span className="font-medium">Transparency</span> page. All proceeds are donated to benefit the Partnership to End Addiction.
            </div>
            <Table data-testid="remittances-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(ledger?.remittances || []).map((r) => (
                  <TableRow key={r.id} data-testid={`admin-remittance-${r.id}`}>
                    <TableCell className="text-stone-700">{r.remitted_at}</TableCell>
                    <TableCell className="font-medium text-emerald-800">{money(r.amount)}</TableCell>
                    <TableCell className="capitalize">{r.method}</TableCell>
                    <TableCell className="text-stone-600">{r.reference || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button data-testid={`delete-remittance-${r.id}`} onClick={() => deleteRemittance(r.id)} variant="ghost" size="sm" className="rounded-full text-stone-400 hover:text-red-600">Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!ledger?.remittances || ledger.remittances.length === 0) && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8">No remittances recorded yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-6 space-y-8">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="admin-requests-panel">
            <div className="p-4 text-sm text-stone-600 border-b border-stone-100 bg-emerald-50/60">Individual help requests shown on the public Donate page. Delete any test or inappropriate entry.</div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Item</TableHead><TableHead>Requester</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {reqs.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-request-${r.id}`}>
                    <TableCell className="font-medium text-stone-900">{r.item_name}</TableCell>
                    <TableCell>{r.requester_name}</TableCell>
                    <TableCell className="capitalize">{r.status}</TableCell>
                    <TableCell className="text-right"><Button data-testid={`delete-request-${r.id}`} onClick={() => deleteRequest(r.id)} variant="ghost" size="sm" className="rounded-full text-red-600">Delete</Button></TableCell>
                  </TableRow>
                ))}
                {reqs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500 py-8">No requests.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="admin-needs-panel">
            <div className="p-4 text-sm text-stone-600 border-b border-stone-100 bg-emerald-50/60">Organization wishlist items shown on the public Donate page.</div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Item</TableHead><TableHead>Organization</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {allNeeds.map((n) => (
                  <TableRow key={n.id} data-testid={`admin-need-${n.id}`}>
                    <TableCell className="font-medium text-stone-900">{n.item_name}</TableCell>
                    <TableCell>{n.org_name}</TableCell>
                    <TableCell className="text-right"><Button data-testid={`delete-need-${n.id}`} onClick={() => deleteNeed(n.id)} variant="ghost" size="sm" className="rounded-full text-red-600">Delete</Button></TableCell>
                  </TableRow>
                ))}
                {allNeeds.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-stone-500 py-8">No wishlist items.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="subscribers" className="mt-6 space-y-6">
          <SubscriberForm onSubmit={addSubscriber} />
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="subscribers-panel">
            <div className="p-4 text-sm text-stone-600 border-b border-stone-100 bg-emerald-50/60">Grocery store subscribers ($499/mo). Add comped subscribers, toggle their subscription, or remove them. Aid organizations are always free and are managed under Approvals.</div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Store</TableHead><TableHead>Contact</TableHead><TableHead>Subscription</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {subscribers.map((s) => (
                  <TableRow key={s.id} data-testid={`subscriber-${s.id}`}>
                    <TableCell><p className="font-medium text-stone-900">{s.org_name}</p><p className="text-xs text-stone-500">{s.city}</p></TableCell>
                    <TableCell className="text-sm text-stone-600">{s.email}<br />{s.phone || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full ${s.subscription_status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-stone-100 text-stone-600"}`}>{s.subscription_status || "inactive"}</Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button data-testid={`toggle-sub-${s.id}`} onClick={() => toggleSub(s.id)} variant="outline" size="sm" className="rounded-full"><Power className="w-3.5 h-3.5 mr-1" />{s.subscription_status === "active" ? "Deactivate" : "Activate"}</Button>
                      <Button data-testid={`delete-sub-${s.id}`} onClick={() => deleteUser(s.id)} variant="ghost" size="sm" className="rounded-full text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {subscribers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500 py-8">No subscribers yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!infoFor} onOpenChange={(o) => !o && setInfoFor(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Request more information</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">Ask <span className="font-medium">{infoFor?.org_name}</span> for documents or details needed to verify them. They'll be notified.</p>
            <Textarea data-testid="info-message" value={infoMsg} onChange={(e) => setInfoMsg(e.target.value)} placeholder="e.g. Please provide your nonprofit registration number or a proof of address." className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button data-testid="info-send" onClick={requestInfo} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function Stat({ icon: Icon, label, value, accent, isText }) {
  const color = accent === "emerald" ? "text-emerald-800 bg-emerald-50" : accent === "orange" ? "text-orange-700 bg-orange-50" : "text-stone-700 bg-stone-100";
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <p className={`font-heading font-semibold text-stone-900 mt-4 ${isText ? "text-2xl" : "text-3xl"}`}>{isText ? value : (value ?? 0)}</p>
      <p className="text-xs tracking-widest uppercase text-stone-500 mt-1">{label}</p>
    </div>
  );
}

function SubscriberForm({ onSubmit }) {
  const [f, setF] = useState({ org_name: "", email: "", password: "", phone: "", city: "" });
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onSubmit(f);
    if (ok) setF({ org_name: "", email: "", password: "", phone: "", city: "" });
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6" data-testid="subscriber-form">
      <h2 className="font-heading text-lg font-medium text-stone-900 flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-800" /> Add a subscriber</h2>
      <p className="text-sm text-stone-500 mt-1">Creates an active grocery-store account. They'll get food-alert emails & texts at the contact info below.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        <Input data-testid="sub-org" required value={f.org_name} onChange={(e) => setF((p) => ({ ...p, org_name: e.target.value }))} placeholder="Store name" className="rounded-xl h-11" />
        <Input data-testid="sub-email" required type="email" value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} placeholder="Email" className="rounded-xl h-11" />
        <Input data-testid="sub-password" required value={f.password} onChange={(e) => setF((p) => ({ ...p, password: e.target.value }))} placeholder="Temp password" className="rounded-xl h-11" />
        <Input data-testid="sub-phone" value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value }))} placeholder="Phone (for SMS alerts)" className="rounded-xl h-11" />
        <Input data-testid="sub-city" value={f.city} onChange={(e) => setF((p) => ({ ...p, city: e.target.value }))} placeholder="City" className="rounded-xl h-11" />
      </div>
      <Button data-testid="sub-submit" type="submit" className="mt-4 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">Add subscriber</Button>
    </form>
  );
}

function RemittanceForm({ onSubmit }) {
  const [f, setF] = useState({ amount: "", method: "ach", reference: "", remitted_at: "", note: "" });
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onSubmit({
      amount: parseFloat(f.amount),
      method: f.method,
      reference: f.reference,
      note: f.note,
      remitted_at: f.remitted_at || undefined,
    });
    if (ok) setF({ amount: "", method: "ach", reference: "", remitted_at: "", note: "" });
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6" data-testid="remittance-form">
      <h2 className="font-heading text-lg font-medium text-stone-900">Record a donation transfer to the charity</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <div className="space-y-1.5">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Amount (USD)</Label>
          <Input data-testid="remittance-amount" required type="number" step="0.01" min="0.01" value={f.amount} onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} className="rounded-xl h-11" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Method</Label>
          <Select value={f.method} onValueChange={(v) => setF((p) => ({ ...p, method: v }))}>
            <SelectTrigger data-testid="remittance-method" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{["ach", "check", "wire"].map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Reference / confirmation #</Label>
          <Input data-testid="remittance-reference" value={f.reference} onChange={(e) => setF((p) => ({ ...p, reference: e.target.value }))} placeholder="ACH-20260701" className="rounded-xl h-11" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-widest uppercase text-stone-500">Date</Label>
          <Input data-testid="remittance-date" type="date" value={f.remitted_at} onChange={(e) => setF((p) => ({ ...p, remitted_at: e.target.value }))} className="rounded-xl h-11" />
        </div>
      </div>
      <Button data-testid="remittance-submit" type="submit" className="mt-4 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">Record payout</Button>
    </form>
  );
}
